import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Input, 
  Select, 
  Modal, 
  Form, 
  message, 
  Typography, 
  Tag, 
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { companyAPI, Company, CompanyFilters } from '../../services/companyAPI';

const { Title } = Typography;
const { Option } = Select;

const CompaniesManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [filters, setFilters] = useState<CompanyFilters>({});
  const [form] = Form.useForm();
  const [generatedSlug, setGeneratedSlug] = useState('');

  // Slug yaratmaq üçün funksiya
  const generateSlug = (name: string): string => {
    if (!name) return '';
    
    // Azərbaycan dilindəki xüsusi hərfləri əvəz et
    const replacements: { [key: string]: string } = {
      'ə': 'e', 'Ə': 'E',
      'ı': 'i', 'I': 'I',
      'ö': 'o', 'Ö': 'O',
      'ü': 'u', 'Ü': 'U',
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ş': 's', 'Ş': 'S'
    };
    
    let slug = name;
    Object.keys(replacements).forEach(key => {
      slug = slug.replace(new RegExp(key, 'g'), replacements[key]);
    });
    
    // Slug formatına çevir
    return slug
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Yalnız hərf, rəqəm, boşluq və tire
      .replace(/\s+/g, '-') // Boşluqları tire ilə əvəz et
      .replace(/-+/g, '-') // Çoxlu tireləri tək tire ilə əvəz et
      .replace(/^-|-$/g, ''); // Başlanğıc və son tireləri sil
  };

  // Form dəyərləri dəyişəndə slug-u yenilə
  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.name !== undefined) {
      const slug = generateSlug(changedValues.name);
      setGeneratedSlug(slug);
      form.setFieldsValue({ slug: slug });
    }
  };

  useEffect(() => {
    loadCompanies();
  }, [filters]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await companyAPI.getCompanies(filters);
      if (response.success && response.data) {
        setCompanies(response.data);
      } else {
        message.error(response.message || 'Şirkətlər yüklənmədi');
      }
    } catch (error) {
      message.error('Şirkətlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    form.resetFields();
    setGeneratedSlug('');
    setModalVisible(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    form.setFieldsValue(company);
    setGeneratedSlug(company.slug || '');
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await companyAPI.deleteCompany(id);
      if (response.success) {
        message.success(response.message || 'Şirkət silindi');
        loadCompanies();
      } else {
        message.error(response.message || 'Şirkət silinmədi');
      }
    } catch (error) {
      message.error('Şirkət silinmədi');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Slug sahəsini həmişə backend-də avtomatik yaranması üçün göndərmə
      const submitData = { ...values };
      delete submitData.slug; // Həm create həm də update zamanı slug göndərmə

      let response: { success: boolean; data?: Company; message?: string; errors?: any };
      if (editingCompany) {
        response = await companyAPI.updateCompany(editingCompany.id, submitData);
      } else {
        response = await companyAPI.createCompany(submitData);
      }

      if (response.success) {
        message.success(response.message || (editingCompany ? 'Şirkət yeniləndi' : 'Şirkət yaradıldı'));
        setModalVisible(false);
        loadCompanies();
      } else {
        message.error(response.message || 'Əməliyyat uğursuz oldu');
        
        // Xətaları göstər
        if (response.errors) {
          Object.keys(response.errors).forEach(key => {
            const errorMessage = Array.isArray(response.errors[key]) 
              ? response.errors[key][0] 
              : response.errors[key];
            
            if (key === 'non_field_errors') {
              message.error(errorMessage);
            } else {
              form.setFields([{
                name: key,
                errors: [errorMessage]
              }]);
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Request failed:', error);
      message.error('Əməliyyat uğursuz oldu: ' + (error.response?.data?.message || error.message));
    }
  };

  const columns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Company) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'company_type',
      key: 'company_type',
      render: (type: string) => (
        <Tag color="blue">{type === 'education' ? 'Təhsil' : type}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'} icon={status === 'active' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {status === 'active' ? 'Aktiv' : 'Deaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Şəhər',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Tələbə Sayı',
      dataIndex: 'student_count',
      key: 'student_count',
      render: (count: number) => (
        <div className="flex items-center">
          <TeamOutlined className="mr-1" />
          {count}
        </div>
      ),
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (_: any, record: Company) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            Redaktə
          </Button>
          <Popconfirm
            title="Şirkəti silmək istədiyinizə əminsiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Bəli"
            cancelText="Xeyr"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="mb-0">
          <BankOutlined className="mr-2" />
          Şirkətlər İdarəetməsi
        </Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleCreate}
        >
          Yeni Şirkət
        </Button>
      </div>

      {/* Statistikalar */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Ümumi Şirkətlər"
              value={companies.length}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Aktiv Şirkətlər"
              value={companies.filter(c => c.status === 'active').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Ümumi Tələbələr"
              value={companies.reduce((sum, c) => sum + c.student_count, 0)}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filterlər */}
      <Card className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Şirkət adı ilə axtarış..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Status seçin"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Aktiv</Option>
              <Option value="inactive">Deaktiv</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Şəhər"
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      {/* Şirkətlər cədvəli */}
      <Card>
        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} şirkət`,
          }}
        />
      </Card>

      {/* Şirkət əlavə/redaktə modalı */}
      <Modal
        title={editingCompany ? 'Şirkəti Redaktə Et' : 'Yeni Şirkət Əlavə Et'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormValuesChange}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Şirkət Adı"
                rules={[{ required: true, message: 'Şirkət adı tələb olunur' }]}
              >
                <Input placeholder="Şirkət adını daxil edin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="slug"
                label={
                  <span>
                    URL Slug 
                    <span style={{ color: '#999', fontWeight: 'normal' }}> 
                      (avtomatik yaranır)
                    </span>
                  </span>
                }
              >
                <Input 
                  placeholder="company-slug" 
                  value={generatedSlug}
                  style={{
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                  }}
                  prefix="/"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="company_type"
                label="Şirkət Tipi"
                rules={[{ required: true, message: 'Şirkət tipi tələb olunur' }]}
              >
                <Select placeholder="Tip seçin">
                  <Option value="educational">Təhsil Müəssisəsi</Option>
                  <Option value="course_center">Kurs Mərkəzi</Option>
                  <Option value="training_center">Təlim Mərkəzi</Option>
                  <Option value="university">Universitet</Option>
                  <Option value="school">Məktəb</Option>
                  <Option value="other">Digər</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Status tələb olunur' }]}
              >
                <Select placeholder="Status seçin">
                  <Option value="active">Aktiv</Option>
                  <Option value="inactive">Qeyri-aktiv</Option>
                  <Option value="suspended">Dayandırılmış</Option>
                  <Option value="closed">Bağlanmış</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="city"
                label="Şəhər"
                rules={[{ required: true, message: 'Şəhər tələb olunur' }]}
              >
                <Input placeholder="Şəhər adını daxil edin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[
                  { pattern: /^(\+994|0)(50|51|55|70|77|99)[0-9]{7}$/, message: 'Düzgün telefon nömrəsi daxil edin!' }
                ]}
              >
                <Input placeholder="+994501234567" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Düzgün email daxil edin' }]}
          >
            <Input placeholder="info@company.az" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Ünvan"
          >
            <Input.TextArea rows={2} placeholder="Şirkət ünvanını daxil edin" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Təsvir"
          >
            <Input.TextArea rows={3} placeholder="Şirkət haqqında qısa məlumat" />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setModalVisible(false)}>
              Ləğv et
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
            >
              {editingCompany ? 'Yenilə' : 'Yarat'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CompaniesManagement;